import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { api, ApiError } from "../api/client";
import type { Me, Role } from "../api/types";
import { SkeletonBlock } from "../components/States";

const ME_KEY = "rai-me";

/** Who is signed in. Offline (no network at all), the last known user is used so
 *  the field recorder still opens; a real 401 always means signed out. */
async function fetchMe(): Promise<Me> {
  try {
    const me = await api<Me>("/api/auth/me");
    try { localStorage.setItem(ME_KEY, JSON.stringify(me)); } catch { /* storage blocked */ }
    return me;
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 401) { try { localStorage.removeItem(ME_KEY); } catch { /* ignore */ } }
      throw e;
    }
    const cached = (() => { try { return JSON.parse(localStorage.getItem(ME_KEY) || "null") as Me | null; } catch { return null; } })();
    if (cached) return cached;
    throw e;
  }
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 2,
    staleTime: 60_000,
  });
}

export const can = {
  review: (r?: Role) => r === "engineer" || r === "admin",
  field: (r?: Role) => r === "surveyor" || r === "engineer" || r === "admin",
  admin: (r?: Role) => r === "admin",
};

/** Sends signed-out visitors to sign in, and keeps pages a role can't use out of reach. */
export function RequireUser({ children, allow }: { children: (me: Me) => ReactNode; allow?: (r: Role) => boolean }) {
  const me = useMe();
  const loc = useLocation();
  if (me.isPending) return <div className="content"><SkeletonBlock rows={6} /></div>;
  if (me.isError) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  if (allow && !allow(me.data.role)) return <Navigate to="/" replace />;
  return <>{children(me.data)}</>;
}
